import React, { useState, useMemo } from 'react';
import ruloAgirlik from './assets/rulo_agirlik.png';
import { LOCALES } from './i18n';
import pulEbat from './assets/pul_ebat.png';
import pul1 from './assets/pul1.png';
import pul2 from './assets/pul2.png';
import pul3 from './assets/pul3.png';
import pul4 from './assets/pul4.png';

// Dizilim görselleri (key → görsel)
const LAYOUT_SVGS = {
    tek: <img src={pul1} alt="" className="max-h-40 w-auto object-contain" />,
    cift: <img src={pul2} alt="" className="max-h-40 w-auto object-contain" />,
    uclu: <img src={pul3} alt="" className="max-h-40 w-auto object-contain" />,
    dortlu: <img src={pul4} alt="" className="max-h-40 w-auto object-contain" />,
};

const ZigzagCalculator = ({ t, lang }) => {
    // Pul özellikleri state - boş başlar, kullanıcı girer
    const [pulOzellikleri, setPulOzellikleri] = useState({
        diameter: '',       // Ø (mm)
        thickness: '',      // t (mm)
        quantity: '',       // Adet
        density: ''         // Yoğunluk (kg/m³)
    });

    // Rulo özellikleri - iç çap girilebilir, tüm dizilimler için aynı
    const [ruloIcCap, setRuloIcCap] = useState('500'); // Ød (mm)

    // Hesaplamalar
    const calculations = useMemo(() => {
        const d = parseFloat(pulOzellikleri.diameter);
        const t = parseFloat(pulOzellikleri.thickness);
        const n = parseFloat(pulOzellikleri.quantity);
        const rho = parseFloat(pulOzellikleri.density);
        const dIc = parseFloat(ruloIcCap);

        // Parça ağırlığı (tüm dizilimler için aynı)
        const parcaAgirlik = Math.PI * Math.pow(d, 2) / 4 * t * rho * Math.pow(10, -9);

        // Tek dizilim
        const tekW = d;
        const tekToplamBoy = d * n;
        const tekAgirlik = tekToplamBoy * t * rho * tekW * Math.pow(10, -9);
        const tekOD = Math.sqrt(tekToplamBoy * 4 * t / Math.PI + Math.pow(dIc, 2));
        const tekPresTabla = tekW * 2 - d;
        const tekFire = (tekAgirlik - parcaAgirlik * n) / n;
        const tekAvantaj = 0;

        // Çift dizilim (zigzag)
        const ciftW = d + d * Math.sqrt(3) / 2;
        const ciftToplamBoy = n % 2 === 0
            ? (n / 2 + 0.5) * d
            : ((n - 1) / 2 + 1) * d;
        const ciftAgirlik = ciftToplamBoy * t * rho * ciftW * Math.pow(10, -9);
        const ciftOD = Math.sqrt(ciftToplamBoy * 4 * t / Math.PI + Math.pow(dIc, 2));
        const ciftPresTabla = ciftW * 2 - d;
        const ciftFire = (ciftAgirlik - parcaAgirlik * n) / n;
        const ciftAvantaj = ((parcaAgirlik + tekFire) - (parcaAgirlik + ciftFire)) / (parcaAgirlik + tekFire);

        // Üçlü dizilim
        const ucluW = d + d * Math.sqrt(3);
        const ucluToplamBoy = d + d / 2 + (n % 3 === 0
            ? ((n - 3) / 3) * d
            : Math.floor((n - 3) / 3) * d + d / 2);
        const ucluAgirlik = ucluToplamBoy * t * rho * ucluW * Math.pow(10, -9);
        const ucluOD = Math.sqrt(ucluToplamBoy * 4 * t / Math.PI + Math.pow(dIc, 2));
        const ucluPresTabla = ucluW * 2 - d;
        const ucluFire = (ucluAgirlik - parcaAgirlik * n) / n;
        const ucluAvantaj = ((parcaAgirlik + tekFire) - (parcaAgirlik + ucluFire)) / (parcaAgirlik + tekFire);

        // Dörtlü dizilim
        const dortluW = d + d * Math.sqrt(3) * 3 / 2;
        const dortluToplamBoy = d + d / 2 + (n % 2 === 0
            ? (n - 4) / 2 * d / 2
            : (n - 3) / 2 * d / 2);
        const dortluAgirlik = dortluToplamBoy * t * rho * dortluW * Math.pow(10, -9);
        const dortluOD = Math.sqrt(dortluToplamBoy * 4 * t / Math.PI + Math.pow(dIc, 2));
        const dortluPresTabla = dortluW * 2 - d;
        const dortluFire = (dortluAgirlik - parcaAgirlik * n) / n;
        const dortluAvantaj = ((parcaAgirlik + tekFire) - (parcaAgirlik + dortluFire)) / (parcaAgirlik + tekFire);

        return {
            parcaAgirlik,
            tek: {
                agirlik: tekAgirlik,
                OD: tekOD,
                Od: dIc,
                W: tekW,
                presTabla: tekPresTabla,
                toplamBoy: tekToplamBoy,
                fire: tekFire,
                avantaj: tekAvantaj
            },
            cift: {
                agirlik: ciftAgirlik,
                OD: ciftOD,
                Od: dIc,
                W: ciftW,
                presTabla: ciftPresTabla,
                toplamBoy: ciftToplamBoy,
                fire: ciftFire,
                avantaj: ciftAvantaj
            },
            uclu: {
                agirlik: ucluAgirlik,
                OD: ucluOD,
                Od: dIc,
                W: ucluW,
                presTabla: ucluPresTabla,
                toplamBoy: ucluToplamBoy,
                fire: ucluFire,
                avantaj: ucluAvantaj
            },
            dortlu: {
                agirlik: dortluAgirlik,
                OD: dortluOD,
                Od: dIc,
                W: dortluW,
                presTabla: dortluPresTabla,
                toplamBoy: dortluToplamBoy,
                fire: dortluFire,
                avantaj: dortluAvantaj
            }
        };
    }, [pulOzellikleri, ruloIcCap]);

    const formatNumber = (num, decimals = 2) => {
        if (isNaN(num) || !isFinite(num)) return '-';
        return num.toLocaleString(LOCALES[lang] || 'tr-TR', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };

    const formatPercent = (num) => {
        if (isNaN(num) || !isFinite(num)) return '-';
        return (num * 100).toFixed(1) + '%';
    };

    const handleInputChange = (field, value) => {
        setPulOzellikleri(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Dizilimleri verimliliğe göre sırala (avantaj yüksek = daha verimli)
    const layoutRanking = useMemo(() => {
        const items = [
            { key: 'tek', label: t.single, avantaj: calculations.tek.avantaj },
            { key: 'cift', label: t.doubleZigzag, avantaj: calculations.cift.avantaj },
            { key: 'uclu', label: t.triple, avantaj: calculations.uclu.avantaj },
            { key: 'dortlu', label: t.quad, avantaj: calculations.dortlu.avantaj },
        ];
        const hasData = items.every(i => Number.isFinite(i.avantaj));
        const sorted = hasData ? [...items].sort((a, b) => b.avantaj - a.avantaj) : items;
        return { sorted, hasData };
    }, [calculations, t]);

    return (
        <div className="max-w-6xl mx-auto">
            {/* Pul Özellikleri Section */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                {/* Başlık - rulo hesaplayıcıdaki gibi kart içinde küçük */}
                <div className="text-base font-bold text-gray-800 mb-3">
                    {t.tabZigzag}
                    <span className="ml-2 text-xs font-medium text-gray-400">{t.zzSubtitle}</span>
                </div>
                <div className="flex flex-col lg:flex-row gap-8 items-center">
                    {/* Sol taraf - Label ve Inputlar */}
                    <div className="flex items-center gap-6">
                        <div className="text-center">
                            <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center shadow-inner">
                                <span className="text-sm font-semibold text-gray-600">{t.discProps}</span>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex items-center gap-3">
                                <label className="bg-gray-100 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 w-36 text-center">
                                    Ø (mm)
                                </label>
                                <input
                                    type="number"
                                    value={pulOzellikleri.diameter}
                                    onChange={(e) => handleInputChange('diameter', e.target.value)}
                                    className="w-24 px-3 py-2 border-2 border-gray-200 rounded-lg text-center font-semibold text-gray-800 focus:border-blue-400 focus:outline-none transition-colors"
                                />
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="bg-gray-100 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 w-36 text-center">
                                    t (mm)
                                </label>
                                <input
                                    type="number"
                                    value={pulOzellikleri.thickness}
                                    onChange={(e) => handleInputChange('thickness', e.target.value)}
                                    className="w-24 px-3 py-2 border-2 border-gray-200 rounded-lg text-center font-semibold text-gray-800 focus:border-blue-400 focus:outline-none transition-colors"
                                />
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="bg-gray-100 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 w-36 text-center">
                                    {t.quantity}
                                </label>
                                <input
                                    type="number"
                                    value={pulOzellikleri.quantity}
                                    onChange={(e) => handleInputChange('quantity', e.target.value)}
                                    className="w-24 px-3 py-2 border-2 border-gray-200 rounded-lg text-center font-semibold text-gray-800 focus:border-blue-400 focus:outline-none transition-colors"
                                />
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="bg-gray-100 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 w-36 text-center">
                                    {t.densityWithUnit}
                                </label>
                                <input
                                    type="number"
                                    value={pulOzellikleri.density}
                                    onChange={(e) => handleInputChange('density', e.target.value)}
                                    className="w-24 px-3 py-2 border-2 border-gray-200 rounded-lg text-center font-semibold text-gray-800 focus:border-blue-400 focus:outline-none transition-colors"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Sağ taraf - Pul Görseli */}
                    <div className="flex-shrink-0">
                      <img src={pulEbat} alt="" width={200}/>
                    </div>
                </div>

                {/* Rulo Özellikleri - iç çap girişi */}
                <div className="flex items-center gap-6 mt-6">
                    <div className="text-center">
                        <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center shadow-inner">
                            <span className="text-sm font-semibold text-gray-600">{t.coilProps}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <label className="bg-gray-100 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 w-36 text-center">
                            Ød (mm)
                        </label>
                        <input
                            type="number"
                            value={ruloIcCap}
                            onChange={(e) => setRuloIcCap(e.target.value)}
                            className="w-24 px-3 py-2 border-2 border-gray-200 rounded-lg text-center font-semibold text-gray-800 focus:border-blue-400 focus:outline-none transition-colors"
                        />
                    </div>
                </div>
            </div>

            {/* Rulo Özellikleri ve Sonuçlar Tablosu */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
                {/* Tablo Header */}
                <div className="grid grid-cols-6 bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                    <div className="p-4 col-span-2"></div>
                    <div className="p-4 text-center font-bold text-gray-700 border-l border-gray-200">{t.single}</div>
                    <div className="p-4 text-center font-bold text-gray-700 border-l border-gray-200">{t.double}</div>
                    <div className="p-4 text-center font-bold text-gray-700 border-l border-gray-200">{t.triple}</div>
                    <div className="p-4 text-center font-bold text-gray-700 border-l border-gray-200">{t.quad}</div>
                </div>

                {/* Rulo Özellikleri Bölümü */}
                <div className="grid grid-cols-6 border-b-2 border-gray-200">
                    {/* Sol görsel ve başlık - 6 satır boyunca */}
                    <div className="row-span-6 p-4 flex items-center justify-center border-r border-gray-100">
                        <div className="flex flex-col items-center gap-2">
                            <img src={ruloAgirlik} alt="" width={150} height={150} />
                            <span className="text-xs text-gray-500 font-medium text-center">{t.coilWeightLabel}</span>
                        </div>
                    </div>

                    {[
                        { label: t.weightKg, vals: [calculations.tek.agirlik, calculations.cift.agirlik, calculations.uclu.agirlik, calculations.dortlu.agirlik], dec: 2 },
                        { label: 'ØD (mm)', vals: [calculations.tek.OD, calculations.cift.OD, calculations.uclu.OD, calculations.dortlu.OD], dec: 1 },
                        { label: 'Ød (mm)', vals: [calculations.tek.Od, calculations.cift.Od, calculations.uclu.Od, calculations.dortlu.Od], dec: 0 },
                        { label: 'W (mm)', vals: [calculations.tek.W, calculations.cift.W, calculations.uclu.W, calculations.dortlu.W], dec: 1 },
                        { label: t.pressTable, vals: [calculations.tek.presTabla, calculations.cift.presTabla, calculations.uclu.presTabla, calculations.dortlu.presTabla], dec: 1 },
                        { label: t.totalLength, vals: [calculations.tek.toplamBoy, calculations.cift.toplamBoy, calculations.uclu.toplamBoy, calculations.dortlu.toplamBoy], dec: 0 },
                    ].map((row, i, arr) => {
                        const rowBorder = i < arr.length - 1 ? 'border-b' : '';
                        return (
                            <React.Fragment key={row.label}>
                                <div className={`p-3 bg-gray-50 text-sm font-medium text-gray-600 flex items-center justify-center ${rowBorder} border-gray-100`}>{row.label}</div>
                                {row.vals.map((v, j) => (
                                    <div key={j} className={`p-3 font-semibold text-gray-800 border-l ${rowBorder} border-gray-100 flex items-center justify-center`}>{formatNumber(v, row.dec)}</div>
                                ))}
                            </React.Fragment>
                        );
                    })}
                </div>

                {/* Verim Bölümü */}
                <div className="grid grid-cols-6 bg-gradient-to-br from-emerald-50 to-teal-50">
                    {/* Sol başlık - 3 satır boyunca */}
                    <div className="row-span-3 p-4 flex items-center justify-center border-r border-gray-200">
                        <div className="text-center">
                            <div className="text-lg font-bold text-emerald-700">{t.efficiency}</div>
                            <div className="text-2xl font-bold text-emerald-600">%</div>
                        </div>
                    </div>

                    {/* Parça */}
                    <div className="p-3 bg-emerald-100/50 text-sm font-medium text-emerald-800 flex items-center justify-center border-b border-gray-200">{t.pieceKg}</div>
                    {[0, 1, 2, 3].map((j) => (
                        <div key={j} className="p-3 font-semibold text-gray-800 border-l border-b border-gray-200 flex items-center justify-center">{formatNumber(calculations.parcaAgirlik, 4)}</div>
                    ))}

                    {/* Fire */}
                    <div className="p-3 bg-emerald-100/50 text-sm font-medium text-emerald-800 flex items-center justify-center border-b border-gray-200">{t.scrapKg}</div>
                    {[calculations.tek.fire, calculations.cift.fire, calculations.uclu.fire, calculations.dortlu.fire].map((v, j) => (
                        <div key={j} className="p-3 font-semibold text-red-600 border-l border-b border-gray-200 flex items-center justify-center">{formatNumber(v, 4)}</div>
                    ))}

                    {/* Avantaj */}
                    <div className="p-3 bg-emerald-100/50 text-sm font-medium text-emerald-800 flex items-center justify-center">{t.advantage}</div>
                    <div className="p-3 font-bold text-gray-500 border-l border-gray-200 flex items-center justify-center">-</div>
                    {[calculations.cift.avantaj, calculations.uclu.avantaj, calculations.dortlu.avantaj].map((v, j) => (
                        <div key={j} className="p-3 font-bold text-emerald-600 border-l border-gray-200 text-lg flex items-center justify-center">{formatPercent(v)}</div>
                    ))}
                </div>
            </div>

            {/* Dizilim Görseli */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-700 mb-4 flex items-center gap-2">
                    <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                    {t.layoutVisual}
                </h3>
                <div className="grid grid-cols-4 gap-4">
                    {layoutRanking.sorted.map((item, idx) => {
                        const isBest = layoutRanking.hasData && idx === 0;
                        return (
                            <div key={item.key} className="text-center">
                                <div className={`relative rounded-xl p-4 h-48 flex items-center justify-center ${
                                    isBest ? 'bg-emerald-50 ring-2 ring-emerald-400' : 'bg-gray-50'
                                }`}>
                                    {layoutRanking.hasData && (
                                        <span className={`absolute top-2 left-2 w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${
                                            isBest ? 'bg-emerald-500 text-white' : 'bg-gray-300 text-gray-700'
                                        }`}>
                                            {idx + 1}
                                        </span>
                                    )}
                                    {LAYOUT_SVGS[item.key]}
                                </div>
                                <p className={`mt-2 text-sm ${
                                    isBest ? 'font-bold text-emerald-600' : 'font-medium text-gray-600'
                                }`}>{item.label}</p>
                                {layoutRanking.hasData && (
                                    <p className={`text-xs font-semibold ${
                                        isBest ? 'text-emerald-500' : 'text-gray-400'
                                    }`}>{formatPercent(item.avantaj)}</p>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ZigzagCalculator;